/**
 * Das Modul-Manifest (KONZEPT.md §3.4).
 *
 * Jedes Modul bringt eine solche Beschreibung mit. Der Core liest sie beim
 * Start, registriert die Rechte, haengt Menue- und Scan-Eintraege ein und
 * prueft, ob das Modul zur laufenden Core-Version passt.
 */
import { z } from "zod";
import { KurzText } from "./basis.js";
import { DashboardEintrag } from "./dashboard.js";
import { EventName } from "./events.js";
/** Bezeichner eines Moduls: klein, ohne Umlaute, ohne Leerzeichen. */
export const ModulName = z
    .string()
    .regex(/^[a-z][a-z0-9_]{1,30}$/, "Modulname: Kleinbuchstaben, Ziffern und Unterstrich");
/**
 * Ein Recht, das ein Modul beim Core registriert, z. B. `ausleihe.ausgeben`.
 * Der Teil vor dem Punkt muss der Modulname sein -- so kann kein Modul Rechte
 * eines anderen definieren.
 */
export const Recht = z.string().regex(/^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/);
export const MenueEintrag = z.object({
    label: KurzText,
    /** Pfad in der Oberflaeche, muss mit dem Modulpfad beginnen. */
    route: z.string().startsWith("/"),
    icon: z.string().max(60).nullable().default(null),
});
/**
 * Eine Aktion, die im Scan-Menue eines Artikels auftaucht (KONZEPT.md §8.2).
 *
 * `{artikelId}` in der Route wird durch die Kennung des gescannten Artikels
 * ersetzt.
 */
export const ScanAktion = z.object({
    label: KurzText,
    route: z.string().startsWith("/"),
    /**
     * Auf welche Artikelarten die Aktion passt. Leer = auf alle.
     * So erscheint "Inhalt / Entnahme" nur an Kleinteilekisten.
     */
    fuer_arten: z.array(z.string()).default([]),
});
export const ModulManifest = z
    .object({
    name: ModulName,
    /** Version des Moduls, semantisch. */
    version: z.string().regex(/^\d+\.\d+\.\d+$/),
    /**
     * Welche Core-Version das Modul vertraegt, als Bereich wie `^1.0`.
     *
     * Der Core prueft das beim Start und verweigert den Dienst statt
     * stillschweigend mit einem unpassenden Modul zu laufen.
     */
    core_api: z.string().min(2),
    /** Eigenes Postgres-Schema (CLAUDE.md §3.1 Regel 2). */
    db_schema: z.string().regex(/^mod_[a-z][a-z0-9_]*$/, "Schemaname muss mit mod_ beginnen"),
    permissions: z.array(Recht).default([]),
    menu: z.array(MenueEintrag).default([]),
    scan_actions: z.array(ScanAktion).default([]),
    /** Kacheln fuer das Dashboard -- Daten, keine Oberflaeche (siehe `dashboard.ts`). */
    dashboard: z.array(DashboardEintrag).default([]),
    subscribes: z.array(EventName).default([]),
})
    .superRefine((m, ctx) => {
    // Ein Modul, das Rechte oder Schemata eines anderen benennt, waere genau
    // das Aufweichen der Grenzen, gegen das die ganze Architektur gebaut ist.
    if (m.db_schema !== `mod_${m.name}`) {
        ctx.addIssue({
            code: "custom",
            path: ["db_schema"],
            message: `Modul "${m.name}" darf nur das Schema "mod_${m.name}" benutzen`,
        });
    }
    for (const recht of m.permissions) {
        if (!recht.startsWith(`${m.name}.`)) {
            ctx.addIssue({
                code: "custom",
                path: ["permissions"],
                message: `Recht "${recht}" gehoert nicht zu Modul "${m.name}"`,
            });
        }
    }
});
//# sourceMappingURL=manifest.js.map